/**
 * utils/contactImportCommit.js
 * Contacts xlsx import, batch 2 of 5 (the COMMIT LAYER): turns an already-resolved import PLAN
 * into the new `data` object, via the exact same pure `addContact`/`validateContactFields`
 * (`contacts.js`) every other write path in this app goes through. No React, no UI, no wizard,
 * no direct storage access — batches 3 (review UI / duplicate resolution) and 4 (Sales -> Person
 * mapping) are what BUILD a plan; batch 5 (the wizard) is what calls this and shows its report.
 *
 * **Everything ambiguous is resolved before this file runs.** This layer does not merge
 * duplicates, does not guess a salesperson, does not invent an address, and does not decide two
 * rows are the same shop — it writes exactly what the plan says, and reports exactly what
 * happened.
 *
 * ── WHAT A PLAN ROW MUST CONTAIN (one row = one contact to attempt to create) ──────────────
 *   {
 *     list: "PUSAT" | "CABANG",
 *     name: string,
 *     addresses: { text: string, isDefault?: boolean }[],   // may be empty; at most one
 *                                                            // isDefault:true is EXPECTED but,
 *                                                            // same as everywhere else in this
 *                                                            // app, never enforced or picked here
 *     ekspedisi: string[],
 *     city: string,
 *     contactPerson: string,
 *     email: string,
 *     keterangan: string,
 *     npwp: string,
 *     personIds: string[],           // real Person ids; >=1 required by validateContactFields
 *     row?: number,                  // optional — the workbook's primary row, carried through
 *     sourceRows?: number[],         // into the report only, for traceability. Never read for
 *                                    // any decision this layer makes.
 *   }
 * Every field maps 1:1 onto `addContact`'s own `fields` argument (`contacts.js`) — this module
 * does no normalization of its own; whatever a row says is exactly what gets written (or
 * rejected), unexamined.
 *
 * ── WHAT THIS LAYER ASSUMES IS ALREADY DECIDED (batches 3 & 4's job, not this one) ─────────
 * - `contactImportParser.js` (batch 1) models `contactPerson`/`email`/`keterangan` (and PUSAT's
 *   `npwp`) as ARRAYS per shop, since a shop can pick up more than one value across its attached
 *   blank rows. `Contact.contactPerson`/`.email`/`.keterangan`/`.npwp` are each a single STRING.
 *   Collapsing an array to the one string a plan row carries (join, pick one, ask a human) is a
 *   later batch's decision — this layer only ever sees the single already-chosen string.
 * - `addresses`/`ekspedisi` stay arrays on both sides (Contact's own shape is already an array
 *   for both), so no collapsing is needed there — except that a later review step may have
 *   DROPPED an address confirmed to be a duplicate of another on the same shop (the corrected
 *   framing: two of the six real "extra addresses" in the source file are exactly this). This
 *   layer has no "is this address a duplicate" logic of its own and will happily create two
 *   identical address entries if a plan row still contains them.
 * - A `possible-second-sales` problem (the corrected framing of what batch 1 originally called
 *   `sales-mismatch` — a blank continuation row's Sales genuinely disagreeing with its parent's
 *   is a possible SECOND salesperson for the same shop, confirmed real via CABANG row 225, never
 *   a fill-down defect to fix) is resolved by the time a plan row reaches this layer: its
 *   `personIds` already carries every Person the human confirmed, one entry or several. This
 *   layer places no upper bound on `personIds.length` — a shop with two real salespeople is
 *   exactly as valid as one with a single one.
 * - Every Sales raw name has already been mapped to a real Person id (batch 4). This layer never
 *   looks up a name, never mints a Person, never guesses one.
 * - Duplicate-named shops (batch 1's `duplicate-name` problem) have already been merged into one
 *   row, or deliberately kept as separate rows with names apart enough not to collide — see
 *   CREATE ONLY below for what happens when a plan still hands this layer two rows that collide.
 *
 * ── CREATE ONLY, NEVER REPLACE ─────────────────────────────────────────────────────────────
 * A shop already in the app — by the APP'S OWN uniqueness rule (`contacts.js`'s `nameCollides`,
 * reused here directly rather than reimplemented, so the import and the app can never disagree
 * about what counts as a duplicate) — is SKIPPED, never overwritten, and reported with its name
 * and the reason. The check is PER LIST: an existing PUSAT contact named "PT ABADI" never blocks
 * creating a CABANG contact of the same name, since the two lists know nothing about each other
 * (same as everywhere else `Contact.list` is used). This also means the SAME rule applies against
 * an app-maintained INTERNAL contact (whose own `.name` is a fixed placeholder) with no special
 * case either way — reusing `nameCollides` verbatim guarantees this without this module having to
 * think about `internal` at all.
 * The check runs against the ACCUMULATING result, not just the app's data at the start of the
 * run — so if a plan somehow still contains two rows with the same name in the same list (a
 * duplicate batch 3 should have resolved but didn't), the first is created and the second is
 * SKIPPED as already-existing, exactly like any other post-existing collision. Nothing is ever
 * merged, and nothing vanishes silently.
 *
 * **VALIDATE FIRST — a broken row is never allowed to hide behind a collision.** `skipped` means
 * "nothing needs doing"; `failed` means "something needs fixing" — a row that is BOTH invalid
 * (e.g. missing `personIds`) AND collides with an existing shop is reported FAILED, with the real
 * validation reason, never silently swallowed as a skip. Only a row that is otherwise fully
 * valid, and collides ONLY on its name, is ever reported skipped. Mechanically: `validateContactFields`
 * itself treats a name collision as just one more validation failure (and short-circuits on it
 * before ever checking `personIds`/`businessId`/labels), so this module locates the one existing
 * contact `nameCollides` would object to (if any, via `nameCollides` itself — reusing it to find
 * the record, never re-deriving the comparison) and passes its `id` as `validateContactFields`'s
 * own `excludeId` — the SAME mechanism `updateContact` already uses to exclude a record's own
 * current name from its own collision check. This makes `validateContactFields` judge everything
 * else about the row on its own merits; only once THAT comes back clean does a real collision
 * turn into a skip instead of a create. The create-only invariant itself is unaffected — a valid
 * row that collides is still never written.
 *
 * ── ONE ATOMIC WRITE ────────────────────────────────────────────────────────────────────────
 * `commitContactImport` is a plain reduce over the plan calling `addContact` directly (mirrors
 * `App.js`'s own `commitImport`/`importCommit.js` pattern for the unrelated catalog import) —
 * zero React renders, zero intermediate writes. The caller (a later batch's `App.js` handler)
 * takes the returned `data` and passes it to ONE `update()` call; an abandoned import never
 * touches `data` at all, since nothing here writes anywhere on its own.
 *
 * ── THE REPORT ──────────────────────────────────────────────────────────────────────────────
 * @typedef {{
 *   totalRows: number,
 *   createdCount: number, skippedCount: number, failedCount: number,
 *   perList: {
 *     PUSAT:  { createdCount: number, skippedCount: number, failedCount: number },
 *     CABANG: { createdCount: number, skippedCount: number, failedCount: number },
 *   },
 *   skipped: { type: "already-exists", list: string, name: string, row: number|null, sourceRows: number[]|null, message: string }[],
 *   failed:  { type: "validation-failed", list: string, name: string, row: number|null, sourceRows: number[]|null, message: string }[],
 * }} ContactImportReport
 * `skipped` is the CREATE-ONLY invariant firing on an OTHERWISE-VALID row (a real, already-existing
 * name and nothing else wrong with it) — `message` is this module's own sentence. `failed` is
 * everything `validateContactFields` rejected regardless of collision (e.g. a row missing
 * `personIds`, whether or not it also collides) — `message` is `validateContactFields`'s OWN
 * error text verbatim, the same sentence any other save path in this app would show for the
 * identical problem. A row can only ever land in exactly one of created / skipped / failed —
 * never dropped without a trace, and never skipped while it's also broken.
 */
import { addContact, validateContactFields, nameCollides } from "./contacts";

function fieldsFromRow(row) {
  return {
    list: row?.list,
    name: row?.name,
    addresses: row?.addresses,
    city: row?.city,
    contactPerson: row?.contactPerson,
    email: row?.email,
    keterangan: row?.keterangan,
    npwp: row?.npwp,
    ekspedisi: row?.ekspedisi,
    personIds: row?.personIds,
  };
}

function traceFields(row) {
  return { row: row?.row ?? null, sourceRows: row?.sourceRows ?? null };
}

/** The one existing contact (if any) that `nameCollides` would object to for this list+name —
 *  found by reusing `nameCollides` itself against each candidate in turn, never by re-deriving
 *  the comparison rule. Used to pass as `validateContactFields`'s own `excludeId` so a real
 *  collision can be judged separately from every other validation rule (see the file header's
 *  "VALIDATE FIRST" section) — the exact same exclusion mechanism `updateContact` already relies
 *  on to exclude a record's own current name from its own collision check. */
function findCollidingContact(contacts, list, name) {
  return (contacts || []).find((c) => nameCollides([c], list, name, null)) || null;
}

/**
 * Commit an already-resolved contact import plan into `existingData`. Pure — returns a new
 * `{data, report}`; never mutates `existingData`, never touches storage/React.
 * @param {object} existingData — the app's current `data` object (reads `.contacts`/`.persons`/
 *   `.businesses`, same as `addContact`/`validateContactFields` already do).
 * @param {object[]} plan — see the file header for the required row shape.
 * @returns {{data: object, report: ContactImportReport}}
 */
export function commitContactImport(existingData, plan) {
  const rows = Array.isArray(plan) ? plan : [];

  const report = {
    totalRows: rows.length,
    createdCount: 0,
    skippedCount: 0,
    failedCount: 0,
    perList: {
      PUSAT: { createdCount: 0, skippedCount: 0, failedCount: 0 },
      CABANG: { createdCount: 0, skippedCount: 0, failedCount: 0 },
    },
    skipped: [],
    failed: [],
  };

  const data = rows.reduce((d, row) => {
    const list = row?.list;
    const name = row?.name;
    const bucket = report.perList[list]; // undefined for a malformed/unrecognized list — see below

    // VALIDATE FIRST (see file header): find whichever existing contact (if any) would collide,
    // then hand its id to validateContactFields as `excludeId` so a real collision is judged
    // SEPARATELY from every other rule — a row that's also broken must fail on that real reason,
    // never disappear behind "already exists" just because it also happens to collide.
    const collidingContact = findCollidingContact(d.contacts || [], list, name);
    const fields = fieldsFromRow(row);
    const check = validateContactFields(d, fields, { excludeId: collidingContact ? collidingContact.id : null });
    if (!check.ok) {
      report.failedCount += 1;
      if (bucket) bucket.failedCount += 1;
      report.failed.push({
        type: "validation-failed",
        list, name, ...traceFields(row),
        message: check.error,
      });
      return d;
    }

    if (collidingContact) {
      report.skippedCount += 1;
      if (bucket) bucket.skippedCount += 1;
      report.skipped.push({
        type: "already-exists",
        list, name, ...traceFields(row),
        message: `Kontak "${name}" sudah ada di daftar ${list}.`,
      });
      return d;
    }

    const nd = addContact(d, fields);
    // Defense in depth: `check` above just ran validateContactFields against this exact `d`/
    // `fields` pair, and addContact re-runs the identical, pure check internally — it cannot
    // rationally refuse here. If it somehow ever does (a future change to either function drifts
    // out of sync), the row must still be accounted for, never silently dropped.
    if (nd === d || (nd.contacts || []).length !== (d.contacts || []).length + 1) {
      report.failedCount += 1;
      if (bucket) bucket.failedCount += 1;
      report.failed.push({
        type: "validation-failed",
        list, name, ...traceFields(row),
        message: "Gagal membuat kontak (sebab tidak diketahui).",
      });
      return d;
    }

    report.createdCount += 1;
    if (bucket) bucket.createdCount += 1;
    return nd;
  }, existingData);

  return { data, report };
}
